import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "stk_cookie_consent_v1";

const DEFAULT_CONSENT = {
  technical: true, // always on
  analytics: false,
  marketing: false,
  decided: false,
  ts: null,
};

function readStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return null;
    return { ...DEFAULT_CONSENT, ...parsed, technical: true };
  } catch {
    return null;
  }
}

function writeStored(value) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    /* ignored */
  }
}

export function useCookieConsent() {
  const [consent, setConsent] = useState(() => readStored() || DEFAULT_CONSENT);

  useEffect(() => {
    if (consent.decided) writeStored(consent);
  }, [consent]);

  const acceptAll = useCallback(() => {
    const next = { technical: true, analytics: true, marketing: true, decided: true, ts: Date.now() };
    setConsent(next);
    writeStored(next);
  }, []);

  const rejectOptional = useCallback(() => {
    const next = { technical: true, analytics: false, marketing: false, decided: true, ts: Date.now() };
    setConsent(next);
    writeStored(next);
  }, []);

  const saveCustom = useCallback((partial) => {
    const next = {
      technical: true,
      analytics: !!partial.analytics,
      marketing: !!partial.marketing,
      decided: true,
      ts: Date.now(),
    };
    setConsent(next);
    writeStored(next);
  }, []);

  const reset = useCallback(() => {
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignored */ }
    setConsent({ ...DEFAULT_CONSENT });
  }, []);

  return { consent, acceptAll, rejectOptional, saveCustom, reset };
}

export const COOKIE_STORAGE_KEY = STORAGE_KEY;
